import { defineCustomElements } from '@ey-xd/motif-components/loader';

let registered = false;

/**
 * Registers the Motif Stencil custom elements.
 *
 * MUST be called exactly once, by the shell, at bootstrap. MFEs MUST NOT call
 * this — `customElements.define()` throws on duplicate registration and would
 * break every Motif component on the page.
 */
export function registerMotifComponents(): void {
  if (registered) return;
  registered = true;
  void defineCustomElements();
  installMotifElementGlobals();
}

/**
 * Stencil Motif components reference their host-element class through globals
 * like `HTMLMotifVerticalNavigationMenuItemElement` (see e.g. the
 * `getMenuElements` method on `motif-vertical-navigation-menu`). The lazy
 * `loader` build exposes these on `window` automatically; the standalone
 * bundles that `@ey-xd/motif-wc-react` lazy-imports do not, which throws
 * `ReferenceError: HTMLMotif...Element is not defined` at runtime when the
 * Stencil component does an `instanceof` check.
 *
 * We re-publish the constructors from `customElements` ourselves once they
 * register. This runs on every `customElements.define` until all expected
 * Motif tags are exported.
 */
function installMotifElementGlobals(): void {
  if (typeof window === 'undefined' || typeof customElements === 'undefined') return;
  const w = window as unknown as Record<string, unknown>;

  const publishOne = (tagName: string) => {
    if (!tagName.startsWith('motif-')) return;
    const ctor = customElements.get(tagName);
    if (!ctor) return;
    const pascal = tagName
      .split('-')
      .map((s) => (s.length ? s[0].toUpperCase() + s.slice(1) : ''))
      .join('');
    const globalName = `HTML${pascal}Element`;
    if (!(globalName in w)) {
      w[globalName] = ctor;
    }
  };

  // Patch `customElements.define` so every future Motif registration also
  // exposes its global.
  const origDefine = customElements.define.bind(customElements);
  customElements.define = function patched(
    name: string,
    ctor: CustomElementConstructor,
    opts?: ElementDefinitionOptions,
  ) {
    origDefine(name, ctor, opts);
    try {
      publishOne(name);
    } catch {
      /* never let the shim break component registration */
    }
  };

  // Also publish anything already registered before the patch landed.
  // `customElements` has no public iterator; we run through the static list of
  // tags Motif ships (the ones we know Stencil reflects). Missing ones just
  // no-op until they show up.
  [
    'motif-vertical-navigation',
    'motif-vertical-navigation-menu',
    'motif-vertical-navigation-menu-item',
    'motif-vertical-navigation-submenu',
    'motif-vertical-navigation-context-switcher',
    'motif-header',
    'motif-footer',
    'motif-button',
    'motif-icon',
    'motif-modal',
    'motif-select',
    'motif-select-option',
    'motif-toaster',
    'motif-toast',
    'motif-progress-loader',
    'motif-avatar',
    'motif-text-link',
  ].forEach(publishOne);
}

export type MotifTheme = 'light' | 'dark';

const THEME_ATTR = 'motif-theme';
const THEME_STORAGE_KEY = 'raptor.shell.motif-theme';

export function getTheme(): MotifTheme {
  const current = document.documentElement.getAttribute(THEME_ATTR);
  return current === 'dark' ? 'dark' : 'light';
}

export function setTheme(theme: MotifTheme): void {
  document.documentElement.setAttribute(THEME_ATTR, theme);
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Storage may be unavailable (private mode, quota); theme still applies for the session.
  }
}

export function restoreTheme(): void {
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    saved = null;
  }
  if (saved === 'light' || saved === 'dark') {
    document.documentElement.setAttribute(THEME_ATTR, saved);
  }
}
